/**
 * =============================================================================
 * ===== FILE: audio.js — SFX + MUSIK PROSEDURAL (Web Audio API) =====
 * =============================================================================
 * Tanpa file MP3 eksternal. Semua suara di-generate di browser.
 *
 * Musik:
 *   playMusic('lobby')   — santai (menu)
 *   playMusic('race')    — semangat (balapan)
 *   playMusic('victory') — fanfare (hasil)
 *
 * SFX: click, countdown, startGun, cheer, finish (tepuk+sorak), eliminate
 * =============================================================================
 */

/* WebAudio SFX + original procedural BGM (no external files).
 * Lobby: chill soft groove · Race: hype energy · Finish: crowd cheer
 */

const NOTE = {
  C2: 65.41, D2: 73.42, E2: 82.41, F2: 87.31, G2: 98.0, A2: 110.0, B2: 123.47,
  C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61, G3: 196.0, A3: 220.0, B3: 246.94,
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.0, A4: 440.0, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.0, C6: 1046.5,
};

function n(name) {
  return NOTE[name] || 0;
}

// Warm lounge chords
const LOBBY_PROGRESSION = [
  [n('F2'), n('A2'), n('C3'), n('E3')], // Fmaj7
  [n('G2'), n('B2'), n('D3'), n('F3')], // G7
  [n('E2') || 82.41, n('G2'), n('B2'), n('D3')], // Em7-ish
  [n('A2'), n('C3'), n('E3'), n('G3')], // Am7
];

const LOBBY_MELODY = [
  'A4', 'C5', 'E5', 'C5', 'D5', 'A4', null, 'G4',
  'E4', 'G4', 'A4', 'C5', 'A4', 'G4', null, null,
  'F4', 'A4', 'C5', 'A4', 'B4', 'G4', 'A4', null,
  'E4', 'G4', 'B4', 'A4', 'G4', 'E4', null, null,
];

// Bright racing power chords / progression
const RACE_PROGRESSION = [
  [n('C3'), n('E3'), n('G3')],
  [n('G2'), n('B2'), n('D3')],
  [n('A2'), n('C3'), n('E3')],
  [n('F2'), n('A2'), n('C3')],
];

const RACE_MELODY = [
  'C5', 'E5', 'G5', 'C6', 'G5', 'E5', 'G5', 'E5',
  'B4', 'D5', 'G5', 'B4', 'D5', 'G5', 'A5', 'G5',
  'A4', 'C5', 'E5', 'A5', 'E5', 'C5', 'E5', 'C5',
  'F4', 'A4', 'C5', 'F5', 'C5', 'A4', 'C5', 'G5',
];

const RACE_BASS = [
  'C2', 'C3', 'C2', 'C3', 'G2', 'G3', 'G2', 'G3',
  'A2', 'A3', 'A2', 'A3', 'F2', 'F3', 'F2', 'F3',
];

/**
 * AudioFX — satu instance global `audio` di akhir file.
 * ensure() wajib dipanggil setelah user gesture (klik).
 */
export class AudioFX {
  /** Siapkan state internal (context dibuat lazy di ensure). */
  constructor() {
    this.ctx = null;
    this.enabled = true;
    this.musicEnabled = true;
    this.master = null;
    this.sfxBus = null;
    this.musicBus = null;
    this.crowdBus = null;

    this._musicMode = null;
    this._timer = null;
    this._nextNoteTime = 0;
    this._step = 0;
    this._bar = 0;
    this._lookahead = 0.1;
    this._scheduleAhead = 0.28;
    this._activeNodes = [];
    this._fadeToken = 0;
    this._resumePromise = null;
    this._cheerTimers = [];
  }

  /**
   * ensure()
   * Create context + buses; resume if suspended (returns Promise).
   * Wajib dipanggil setelah user tap (kebijakan autoplay browser).
   */
  ensure() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return Promise.resolve(null);
      this.ctx = new AC();

      this.master = this.ctx.createGain();
      this.master.gain.value = 1;
      this.master.connect(this.ctx.destination);

      this.sfxBus = this.ctx.createGain();
      this.sfxBus.gain.value = 0.55;
      this.sfxBus.connect(this.master);

      this.musicBus = this.ctx.createGain();
      this.musicBus.gain.value = 0.0001;
      this.musicBus.connect(this.master);

      this.crowdBus = this.ctx.createGain();
      this.crowdBus.gain.value = 0.7;
      this.crowdBus.connect(this.master);
    }

    if (this.ctx.state === 'suspended') {
      if (!this._resumePromise) {
        this._resumePromise = this.ctx
          .resume()
          .catch(() => {})
          .finally(() => {
            this._resumePromise = null;
          });
      }
      return this._resumePromise.then(() => this.ctx);
    }
    return Promise.resolve(this.ctx);
  }

  /** Sync helper when caller doesn't await */
  ensureSync() {
    this.ensure();
    return this.ctx;
  }

  toggle() {
    this.enabled = !this.enabled;
    if (!this.enabled) {
      this._stopScheduler();
      this._clearCheerTimers();
      this._killVoices(0.05);
      if (this.musicBus && this.ctx) {
        this.musicBus.gain.cancelScheduledValues(this.ctx.currentTime);
        this.musicBus.gain.setTargetAtTime(0.0001, this.ctx.currentTime, 0.03);
      }
    } else {
      const mode = this._musicMode || 'lobby';
      this.playMusic(mode, true);
    }
    return this.enabled;
  }

  /* =========================================================================
   * SFX — efek suara pendek (klik, beep, tembakan start, dll.)
   * ========================================================================= */

  /** Nada pendek oscillator (beep/klik/melodi kecil). */
  tone(freq, dur = 0.15, type = 'sine', gain = 0.2, slide = 0, bus = null) {
    if (!this.enabled) return;
    const ctx = this.ensureSync();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (slide) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(40, freq + slide), t0 + dur);
    }
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(Math.max(gain, 0.0001), t0 + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g);
    g.connect(bus || this.sfxBus);
    osc.start(t0);
    osc.stop(t0 + dur + 0.03);
  }

  noise(dur = 0.2, gain = 0.12, filterFreq = 800, type = 'lowpass', bus = null) {
    if (!this.enabled) return;
    const ctx = this.ensureSync();
    if (!ctx) return;
    const samples = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, samples, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < samples; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / samples, 0.6);
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = filterFreq;
    const g = ctx.createGain();
    g.gain.value = gain;
    src.connect(filter);
    filter.connect(g);
    g.connect(bus || this.sfxBus);
    src.start();
  }

  countdownBeep(step) {
    if (step <= 0) this.tone(880, 0.35, 'square', 0.2, 200);
    else this.tone(440 + step * 40, 0.18, 'triangle', 0.18);
  }

  startGun() {
    this.noise(0.25, 0.25, 1200);
    this.tone(180, 0.2, 'sawtooth', 0.12, -80);
  }

  /** Light short cheer (mid-race moments) */
  cheer() {
    if (!this.enabled) return;
    for (let i = 0; i < 8; i++) {
      const t = setTimeout(() => {
        this.tone(500 + Math.random() * 700, 0.1, 'triangle', 0.05);
      }, i * 35);
      this._cheerTimers.push(t);
    }
    this.noise(0.45, 0.1, 2400, 'bandpass');
  }

  /**
   * finish()
   * Big finish celebration: fanfare + layered applause + crowd roar + whoops.
   * Dipanggil dari race.js saat balapan selesai.
   */
  finish() {
    if (!this.enabled) return;
    const ctx = this.ensureSync();
    if (!ctx) return;

    // Fanfare
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5];
    notes.forEach((f, i) => {
      const t = setTimeout(() => this.tone(f, 0.32, 'sine', 0.16), i * 85);
      this._cheerTimers.push(t);
    });

    // Sustained applause + roar
    this._playApplause(3.8);
    this._playCrowdRoar(3.2);

    // Excited whoops / "woo" pitches
    for (let i = 0; i < 18; i++) {
      const t = setTimeout(() => {
        if (!this.enabled) return;
        const base = 420 + Math.random() * 680;
        this.tone(base, 0.18 + Math.random() * 0.2, 'triangle', 0.04 + Math.random() * 0.04, 80 + Math.random() * 160, this.crowdBus);
        if (Math.random() > 0.4) {
          this.tone(base * 1.5, 0.12, 'sine', 0.03, 40, this.crowdBus);
        }
      }, 120 + i * 140 + Math.random() * 80);
      this._cheerTimers.push(t);
    }

    // Clap bursts in waves
    for (let w = 0; w < 12; w++) {
      const t = setTimeout(() => this._clapBurst(6 + Math.floor(Math.random() * 8)), 200 + w * 220);
      this._cheerTimers.push(t);
    }
  }

  _clearCheerTimers() {
    for (const t of this._cheerTimers) clearTimeout(t);
    this._cheerTimers = [];
  }

  /** Dense filtered noise shaped like applause */
  _playApplause(duration = 3.5) {
    const ctx = this.ensureSync();
    if (!ctx || !this.enabled) return;
    const t0 = ctx.currentTime;
    const sr = ctx.sampleRate;
    const len = Math.floor(sr * duration);
    const buf = ctx.createBuffer(2, len, sr);

    for (let ch = 0; ch < 2; ch++) {
      const data = buf.getChannelData(ch);
      let env = 0;
      for (let i = 0; i < len; i++) {
        const tt = i / sr;
        // swell in, hold, fade
        const attack = Math.min(1, tt / 0.25);
        const release = tt > duration - 1.1 ? Math.max(0, (duration - tt) / 1.1) : 1;
        // clap-ish impulses
        if (Math.random() < 0.12) env = 0.6 + Math.random() * 0.9;
        env *= 0.86;
        const crackle = (Math.random() * 2 - 1) * (0.15 + env);
        const soft = (Math.random() * 2 - 1) * 0.12;
        data[i] = (crackle + soft) * attack * release * 0.55;
      }
    }

    const src = ctx.createBufferSource();
    src.buffer = buf;

    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1800;
    bp.Q.value = 0.7;

    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 600;

    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(0.55, t0 + 0.3);
    g.gain.setValueAtTime(0.5, t0 + duration - 1);
    g.gain.linearRampToValueAtTime(0.0001, t0 + duration);

    src.connect(bp);
    bp.connect(hp);
    hp.connect(g);
    g.connect(this.crowdBus);
    src.start(t0);
    src.stop(t0 + duration + 0.05);
  }

  /** Low mid "stadium roar" bed */
  _playCrowdRoar(duration = 3) {
    const ctx = this.ensureSync();
    if (!ctx || !this.enabled) return;
    const t0 = ctx.currentTime;
    const sr = ctx.sampleRate;
    const len = Math.floor(sr * duration);
    const buf = ctx.createBuffer(1, len, sr);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) {
      const tt = i / sr;
      const attack = Math.min(1, tt / 0.4);
      const release = tt > duration - 0.9 ? Math.max(0, (duration - tt) / 0.9) : 1;
      data[i] = (Math.random() * 2 - 1) * attack * release;
    }
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 900;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 450;
    bp.Q.value = 0.8;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(0.35, t0 + 0.35);
    g.gain.linearRampToValueAtTime(0.0001, t0 + duration);
    src.connect(lp);
    lp.connect(bp);
    bp.connect(g);
    g.connect(this.crowdBus);
    src.start(t0);
    src.stop(t0 + duration + 0.05);
  }

  _clapBurst(count = 8) {
    if (!this.enabled) return;
    for (let i = 0; i < count; i++) {
      const t = setTimeout(() => {
        this.noise(0.04 + Math.random() * 0.03, 0.08 + Math.random() * 0.06, 1500 + Math.random() * 2000, 'bandpass', this.crowdBus);
      }, i * (18 + Math.random() * 22));
      this._cheerTimers.push(t);
    }
  }

  eliminate() {
    this.tone(220, 0.25, 'sawtooth', 0.1, -120);
  }

  click() {
    this.tone(660, 0.05, 'square', 0.06);
  }

  /* =========================================================================
   * MUSIC API — BGM loop prosedural (scheduler step 1/8 nada)
   * ========================================================================= */

  /**
   * playMusic(mode, forceRestart)
   * @param {'lobby'|'race'|'victory'|null} mode
   * @param {boolean} forceRestart
   */
  async playMusic(mode, forceRestart = false) {
    if (mode === this._musicMode && !forceRestart && this._timer) return;
    this._musicMode = mode;

    if (!this.enabled || !this.musicEnabled || !mode) {
      this._fadeMusicOut();
      this._stopScheduler();
      return;
    }

    const ctx = await this.ensure();
    if (!ctx) return;

    // Wait a tick if still suspended
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        /* ignore */
      }
    }

    this._stopScheduler();
    this._killVoices(0.03);

    const targetVol =
      mode === 'lobby' ? 0.48 : mode === 'race' ? 0.42 : mode === 'victory' ? 0.36 : 0.3;

    const t = ctx.currentTime;
    this.musicBus.gain.cancelScheduledValues(t);
    const cur = Math.max(this.musicBus.gain.value, 0.0001);
    this.musicBus.gain.setValueAtTime(cur, t);
    this.musicBus.gain.linearRampToValueAtTime(targetVol, t + 0.45);

    this._step = 0;
    this._bar = 0;
    this._nextNoteTime = ctx.currentTime + 0.08;
    this._startScheduler();
  }

  stopMusic() {
    this._musicMode = null;
    this._fadeMusicOut();
    this._stopScheduler();
  }

  _fadeMusicOut() {
    if (!this.ctx || !this.musicBus) return;
    const t = this.ctx.currentTime;
    const token = ++this._fadeToken;
    this.musicBus.gain.cancelScheduledValues(t);
    this.musicBus.gain.setValueAtTime(Math.max(this.musicBus.gain.value, 0.0001), t);
    this.musicBus.gain.linearRampToValueAtTime(0.0001, t + 0.4);
    setTimeout(() => {
      if (token === this._fadeToken) this._killVoices(0.02);
    }, 450);
  }

  _startScheduler() {
    this._stopScheduler();
    const tick = () => {
      if (!this.enabled || !this.musicEnabled || !this._musicMode || !this.ctx) return;
      if (this.ctx.state === 'suspended') {
        this.ctx.resume().catch(() => {});
        this._timer = window.setTimeout(tick, 120);
        return;
      }
      const ctx = this.ctx;
      // Prevent spiral if tab lagged
      if (this._nextNoteTime < ctx.currentTime - 0.5) {
        this._nextNoteTime = ctx.currentTime + 0.05;
      }
      while (this._nextNoteTime < ctx.currentTime + this._scheduleAhead) {
        this._scheduleStep(this._nextNoteTime, this._step, this._bar);
        this._advanceStep();
      }
      this._timer = window.setTimeout(tick, this._lookahead * 1000);
    };
    tick();
  }

  _stopScheduler() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  _advanceStep() {
    const mode = this._musicMode;
    // Distinct tempos
    const bpm = mode === 'lobby' ? 92 : mode === 'victory' ? 112 : 142;
    const stepDur = 60 / bpm / 2; // 8th notes
    this._nextNoteTime += stepDur;
    this._step += 1;
    if (this._step % 8 === 0) this._bar += 1;
  }

  _scheduleStep(time, step, bar) {
    const mode = this._musicMode;
    if (mode === 'lobby') this._scheduleLobby(time, step, bar);
    else if (mode === 'race') this._scheduleRace(time, step, bar);
    else if (mode === 'victory') this._scheduleVictory(time, step, bar);
  }

  /** Satu step musik LOBBY (pad + bass lembut + melodi). */
  _scheduleLobby(time, step, bar) {
    const beat = step % 8;
    const chord = LOBBY_PROGRESSION[Math.floor(bar % 4)];

    // Lush pad each bar
    if (beat === 0) {
      for (const f of chord) {
        this._pad(time, f, 2.1, 0.09, 'sine');
        this._pad(time, f * 2, 2.1, 0.035, 'triangle');
      }
    }

    // Walking soft bass
    if (beat === 0 || beat === 3 || beat === 4 || beat === 6) {
      const bassNote = beat === 3 || beat === 6 ? chord[1] * 0.5 : chord[0] * 0.5;
      this._bass(time, bassNote, 0.42, 0.14, 'triangle');
    }

    // Soft kick on 1 & 3
    if (beat === 0 || beat === 4) this._kick(time, 0.09, 95);
    // Side stick feel
    if (beat === 2 || beat === 6) this._hat(time, 0.04, 0.05);
    if (beat % 2 === 1) this._hat(time, 0.028, 0.035);

    // Melody — more present
    const m = LOBBY_MELODY[step % LOBBY_MELODY.length];
    if (m) {
      this._pluck(time, n(m), 0.5, 0.11, 'sine');
      this._pluck(time + 0.16, n(m) * 0.5, 0.4, 0.04, 'triangle');
    }

    // Shimmer
    if (beat === 5 && bar % 2 === 0) {
      this._pluck(time, n('E5'), 0.35, 0.06, 'sine');
    }
  }

  /** Satu step musik RACE (kick/snare/bass/lead hype). */
  _scheduleRace(time, step, bar) {
    const beat = step % 8;
    const chord = RACE_PROGRESSION[Math.floor((bar % 8) / 2) % 4];

    // Four-on-the-floor + pump
    if (beat === 0 || beat === 2 || beat === 4 || beat === 6) this._kick(time, 0.18, 130);
    if (beat === 1 || beat === 3 || beat === 5 || beat === 7) this._hat(time, 0.05, 0.03);

    // Snare backbeat
    if (beat === 2 || beat === 6) this._snare(time, 0.1);
    if (beat === 4) this._snare(time, 0.05);

    // Driving bass 16th-ish feel on 8ths
    const bf = n(RACE_BASS[step % RACE_BASS.length]);
    if (bf) this._bass(time, bf, 0.18, 0.16, 'sawtooth');

    // Power stabs
    if (beat === 0) {
      for (const f of chord) this._stab(time, f, 0.32, 0.07);
    } else if (beat === 4) {
      for (const f of chord) this._stab(time, f * 1.01, 0.2, 0.045);
    }

    // Hype lead
    const m = RACE_MELODY[step % RACE_MELODY.length];
    if (m) this._lead(time, n(m), 0.18, 0.08);

    // Build fills
    if (bar % 4 === 3 && beat >= 4) {
      this._hat(time, 0.07, 0.04);
      if (beat === 7) {
        this._snare(time, 0.12);
        this._lead(time, n('C6'), 0.22, 0.07);
      }
    }
  }

  /** Satu step musik VICTORY (fanfare loop pendek). */
  _scheduleVictory(time, step) {
    const beat = step % 8;
    const fanfare = ['C5', 'E5', 'G5', 'C6', 'G5', 'E5', 'G5', 'C5'];
    if (beat === 0 || beat === 4) this._kick(time, 0.1, 105);
    if (beat === 2 || beat === 6) this._snare(time, 0.07);
    const m = fanfare[beat];
    if (m) this._lead(time, n(m), 0.3, 0.08);
    if (beat === 0) {
      for (const f of [n('C4'), n('E4'), n('G4'), n('C5')]) {
        this._pad(time, f, 1.3, 0.06, 'triangle');
      }
    }
  }

  // ─── synth voices ────────────────────────────────────
  _track(node) {
    this._activeNodes.push(node);
    if (this._activeNodes.length > 100) {
      const old = this._activeNodes.shift();
      try {
        old.stop?.();
      } catch {
        /* ignore */
      }
    }
  }

  _killVoices() {
    const ctx = this.ctx;
    const t = ctx ? ctx.currentTime : 0;
    for (const node of this._activeNodes) {
      try {
        node.stop?.(t + 0.02);
      } catch {
        /* ignore */
      }
    }
    this._activeNodes = [];
  }

  _envGain(time, attack, decay, sustain, release, peak, dur) {
    const ctx = this.ctx;
    const g = ctx.createGain();
    const pk = Math.max(peak, 0.0001);
    const t0 = time;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(pk, t0 + Math.max(attack, 0.005));
    g.gain.exponentialRampToValueAtTime(Math.max(pk * sustain, 0.0001), t0 + attack + decay);
    const end = t0 + dur;
    g.gain.setValueAtTime(
      Math.max(pk * sustain, 0.0001),
      Math.max(t0 + attack + decay, end - release)
    );
    g.gain.exponentialRampToValueAtTime(0.0001, end);
    return g;
  }

  _pad(time, freq, dur, peak, type = 'sine') {
    if (!freq) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = this._envGain(time, 0.2, 0.35, 0.6, 0.45, peak, dur);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1100, time);
    filter.frequency.linearRampToValueAtTime(1600, time + dur * 0.5);
    osc.type = type;
    osc.frequency.value = freq;
    osc.detune.value = (Math.random() - 0.5) * 6;
    osc.connect(filter);
    filter.connect(g);
    g.connect(this.musicBus);
    osc.start(time);
    osc.stop(time + dur + 0.05);
    this._track(osc);
  }

  _pluck(time, freq, dur, peak, type = 'sine') {
    if (!freq) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = this._envGain(time, 0.008, 0.1, 0.25, 0.22, peak, dur);
    osc.type = type;
    osc.frequency.setValueAtTime(freq, time);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.985, time + dur);
    osc.connect(g);
    g.connect(this.musicBus);
    osc.start(time);
    osc.stop(time + dur + 0.05);
    this._track(osc);
  }

  _bass(time, freq, dur, peak, type = 'triangle') {
    if (!freq) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = this._envGain(time, 0.008, 0.07, 0.4, 0.1, peak, dur);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = type === 'sawtooth' ? 520 : 320;
    osc.type = type;
    osc.frequency.value = freq;
    osc.connect(filter);
    filter.connect(g);
    g.connect(this.musicBus);
    osc.start(time);
    osc.stop(time + dur + 0.05);
    this._track(osc);
  }

  _lead(time, freq, dur, peak) {
    if (!freq) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const g = this._envGain(time, 0.008, 0.07, 0.35, 0.1, peak, dur);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2600, time);
    filter.Q.value = 3;
    osc.type = 'square';
    osc2.type = 'triangle';
    osc.frequency.value = freq;
    osc2.frequency.value = freq;
    osc2.detune.value = 8;
    osc.connect(filter);
    osc2.connect(filter);
    filter.connect(g);
    g.connect(this.musicBus);
    osc.start(time);
    osc2.start(time);
    osc.stop(time + dur + 0.05);
    osc2.stop(time + dur + 0.05);
    this._track(osc);
    this._track(osc2);
  }

  _stab(time, freq, dur, peak) {
    if (!freq) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = this._envGain(time, 0.004, 0.07, 0.18, 0.1, peak, dur);
    osc.type = 'sawtooth';
    osc.frequency.value = freq;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2000, time);
    filter.frequency.exponentialRampToValueAtTime(450, time + dur);
    osc.connect(filter);
    filter.connect(g);
    g.connect(this.musicBus);
    osc.start(time);
    osc.stop(time + dur + 0.05);
    this._track(osc);
  }

  _kick(time, peak = 0.12, startF = 120) {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(startF, time);
    osc.frequency.exponentialRampToValueAtTime(42, time + 0.14);
    g.gain.setValueAtTime(peak, time);
    g.gain.exponentialRampToValueAtTime(0.0001, time + 0.2);
    osc.connect(g);
    g.connect(this.musicBus);
    osc.start(time);
    osc.stop(time + 0.22);
    this._track(osc);
  }

  _snare(time, peak = 0.06) {
    const ctx = this.ctx;
    const dur = 0.14;
    const samples = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, samples, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < samples; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 1100;
    const g = ctx.createGain();
    g.gain.setValueAtTime(peak, time);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.musicBus);
    src.start(time);
    src.stop(time + dur);
    this._track(src);

    const osc = ctx.createOscillator();
    const g2 = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(180, time);
    osc.frequency.exponentialRampToValueAtTime(100, time + 0.08);
    g2.gain.setValueAtTime(peak * 0.55, time);
    g2.gain.exponentialRampToValueAtTime(0.0001, time + 0.1);
    osc.connect(g2);
    g2.connect(this.musicBus);
    osc.start(time);
    osc.stop(time + 0.12);
    this._track(osc);
  }

  _hat(time, peak = 0.03, dur = 0.04) {
    const ctx = this.ctx;
    const samples = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, samples, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < samples; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 6500;
    const g = ctx.createGain();
    g.gain.setValueAtTime(peak, time);
    g.gain.exponentialRampToValueAtTime(0.0001, time + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.musicBus);
    src.start(time);
    src.stop(time + dur);
    this._track(src);
  }
}

export const audio = new AudioFX();
